import type { IntlShape, MessageDescriptor } from 'react-intl';
import type { $ZodErrorMap } from 'zod/v4/core';
import { errorCommonI18n } from '@_/i18n-common';

type MakeZodI18nMap = (options: { intl: IntlShape }) => $ZodErrorMap;

export const createZodErrorMap: MakeZodI18nMap =
  ({ intl }) =>
  issue => {
    switch (issue.code) {
      case 'invalid_type':
        if (issue.input === undefined || issue.input === null) {
          return intl.formatMessage(errorCommonI18n.required);
        }

        break;
      case 'invalid_format':
        if (issue.format === 'email') {
          return intl.formatMessage(errorCommonI18n.invalidEmail);
        }

        if (issue.format === 'date' || issue.format === 'datetime') {
          return intl.formatMessage(errorCommonI18n.invalidDate);
        }

        if (issue.format === 'regex') {
          return intl.formatMessage({
            id: 'form-validators:error.zod.invalid-input',
            defaultMessage: 'Invalid input'
          });
        }

        break;
      case 'too_small': {
        if (issue.origin === 'string') {
          if (issue.minimum === 1) {
            return intl.formatMessage(errorCommonI18n.required);
          }

          if (issue.inclusive) {
            return intl.formatMessage(
              {
                id: 'form-validators:error.zod.string-too-short-inclusive',
                defaultMessage: 'Must be at least {minimum} characters'
              },
              {
                minimum: issue.minimum
              }
            );
          } else {
            return intl.formatMessage(
              {
                id: 'form-validators:error.zod.string-too-short',
                defaultMessage: 'Must be more than {minimum} characters'
              },
              {
                minimum: issue.minimum
              }
            );
          }
        }

        if (issue.origin === 'number') {
          if (issue.inclusive) {
            return intl.formatMessage(
              {
                id: 'form-validators:error.zod.number-too-small-inclusive',
                defaultMessage: 'Must be {minimum} or greater'
              },
              {
                minimum: issue.minimum
              }
            );
          } else {
            return intl.formatMessage(
              {
                id: 'form-validators:error.zod.number-too-small',
                defaultMessage: 'Must be greater than {minimum}'
              },
              {
                minimum: issue.minimum
              }
            );
          }
        }

        break;
      }
      case 'too_big': {
        if (issue.origin === 'string') {
          if (issue.inclusive) {
            return intl.formatMessage(
              {
                id: 'form-validators:error.zod.string-too-long-inclusive',
                defaultMessage: 'Must be at most {maximum} characters'
              },
              {
                maximum: issue.maximum
              }
            );
          } else {
            return intl.formatMessage(
              {
                id: 'form-validators:error.zod.string-too-long',
                defaultMessage: 'Must be shorter than {maximum} characters'
              },
              {
                maximum: issue.maximum
              }
            );
          }
        }

        if (issue.origin === 'number') {
          if (issue.inclusive) {
            return intl.formatMessage(
              {
                id: 'form-validators:error.zod.number-too-big-inclusive',
                defaultMessage: 'Must be {maximum} or less'
              },
              {
                maximum: issue.maximum
              }
            );
          } else {
            return intl.formatMessage(
              {
                id: 'form-validators:error.zod.number-too-big',
                defaultMessage: 'Must be less than {maximum}'
              },
              {
                maximum: issue.maximum
              }
            );
          }
        }

        break;
      }
      case 'custom':
        if (issue.params && 'i18nMessage' in issue.params) {
          const i18nMessage = issue.params.i18nMessage as MessageDescriptor;
          return intl.formatMessage(i18nMessage);
        }

        break;
      default:
        break;
    }
  };
